import { ReactElement, useState, useEffect, useMemo } from 'react';
import { Button } from '@/components/Button';
import copyIcon from '@/assets/images/common/copy.svg';
import { apiPostRequest, apiGetRequest } from '@/utils/apiClient';
import Modal from '@/components/Modal';
import ReferralProgramGuide from './ReferralProgramGuide';
import { useTranslation } from 'react-i18next';
import { createDateFormatter } from '@/i18n/dateFormats';
import { usePanelLoading } from '@/components/PanelLoadingBoundary';

/**
 * ユーザー紹介情報パネル
 * デスク画面に表示される紹介プログラムの情報を表示
 * @returns ユーザー紹介情報パネル
 */
function UserReferralInfo(): ReactElement {
    const { t, i18n } = useTranslation('referral');
    // 紹介コード発行状態（仮データ）
    const [hasReferralCode, setHasReferralCode] = useState<boolean>(false);
    const [referralCode, setReferralCode] = useState<string>('');
    const [isCopied, setIsCopied] = useState<boolean>(false);
    const [isInfoOpen, setIsInfoOpen] = useState<boolean>(false);

    // 紹介実績
    const [referralCount, setReferralCount] = useState<number>(0);
    const maxReferrals = 36;

    // 紹介特典情報
    const [benefit, setBenefit] = useState<any>(null);
    const { setLoading } = usePanelLoading();
    const [isPanelLoading, setIsPanelLoading] = useState<boolean>(true);

    const numberFormatter = useMemo(() => new Intl.NumberFormat(i18n.language), [i18n.language]);
    const dateFormatter = useMemo(
        () => createDateFormatter(i18n.language, 'dateMedium'),
        [i18n.language],
    );

    // 有効期限の計算とフォーマット
    const getFormattedExpirationDate = () => {
        if (!benefit || !benefit.isActive || !benefit.endAt) return t('panel.plan.none');

        // UTC時間をJST（日本時間）に変換してフォーマット
        const endDate = new Date(benefit.endAt);
        return dateFormatter.format(endDate);
    };

    const isExpired = (() => {
        if (!benefit || !benefit.isActive || !benefit.endAt) return false;
        const endOfDay = new Date(benefit.endAt);
        endOfDay.setHours(23, 59, 59, 999);
        return Date.now() > endOfDay.getTime();
    })();

    // 紹介リンク生成
    const generateReferralLink = (code: string): string => {
        const origin =
            typeof window !== 'undefined' ? window.location.origin : 'https://kotomil.app';
        return `${origin}/?ref=${encodeURIComponent(code)}`;
    };

    // 紹介コード発行ボタン押下
    const handleIssueReferralCode = async (): Promise<void> => {
        try {
            setIsPanelLoading(true);
            const response = await apiPostRequest('/api/user/referral/issue', {});

            if (response.ok) {
                const data = await response.json();
                setReferralCode(data.code);
                setHasReferralCode(true);
                // 発行成功時に説明モーダルを表示
                setIsInfoOpen(true);
            } else {
                const errorData = await response.json();
                console.error('紹介コード発行エラー:', errorData);
                alert(t('panel.errors.issue'));
            }
        } catch (error) {
            console.error('API呼び出しエラー:', error);
            alert(t('panel.errors.issue'));
        } finally {
            setIsPanelLoading(false);
        }
    };

    // 紹介リンクをクリップボードにコピー
    const handleCopyCode = async (): Promise<void> => {
        try {
            const referralLink = hasReferralCode ? generateReferralLink(referralCode) : '';
            await navigator.clipboard.writeText(referralLink);
            setIsCopied(true);
            // 2秒後に元の状態に戻す
            setTimeout(() => {
                setIsCopied(false);
            }, 2000);
        } catch (err) {
            console.error('コピーに失敗しました:', err);
            alert(t('panel.errors.copy'));
        }
    };

    // 初期化時に既存の紹介情報を取得
    useEffect(() => {
        const fetchReferralInfo = async () => {
            try {
                setIsPanelLoading(true);
                const response = await apiGetRequest('/api/user/referral/info');

                if (response.ok) {
                    const data = await response.json();
                    if (data.hasReferralCode) {
                        setReferralCode(data.code);
                        setHasReferralCode(true);
                    }
                    // 紹介数を設定（紹介コードがない場合も0が返される）
                    setReferralCount(data.referralCount || 0);
                    // 紹介特典情報を設定
                    setBenefit(data.benefit);
                } else {
                    console.error('紹介情報取得エラー:', response.status);
                }
            } catch (error) {
                console.error('紹介情報取得API呼び出しエラー:', error);
            } finally {
                setIsPanelLoading(false);
            }
        };

        fetchReferralInfo();
    }, []);

    useEffect(() => {
        setLoading(isPanelLoading);
    }, [isPanelLoading, setLoading]);

    return (
        <>
            <div className="h-full w-full p-4 relative flex flex-col">
                {/* インフォメーションボタン（右上） */}
                <button
                    type="button"
                    aria-label={t('panel.ariaInfo')}
                    title={t('panel.ariaInfo')}
                    className="absolute top-2 right-2 w-7 h-7 rounded-full border border-gray-300 text-gray-400 hover:bg-gray-50 flex items-center justify-center"
                    onClick={() => setIsInfoOpen(true)}
                >
                    <span className="text-sm font-semibold">i</span>
                </button>

                <div className="flex-1 flex flex-col justify-center">
                    {/* ヘッダー */}
                    <div className="flex items-center justify-center mb-6">
                        <h3 className="text-lg text-gray-800">{t('panel.title')}</h3>
                    </div>

                    {/* 項目リスト */}
                    <div className="space-y-2">
                        {/* 紹介URL */}
                        <div className="flex items-center justify-between py-2 border-b border-gray-100">
                            <div className="flex-shrink-0 w-24">
                                <span className="text-base text-gray-700">{t('panel.link.label')}</span>
                            </div>
                            <div className="flex-1 min-w-0 ml-4">
                                {!hasReferralCode ? (
                                    <Button
                                        variant="outline"
                                        className="w-32 text-sm py-2 bg-kotomilThemaColor border-kotomilThemaColor"
                                        onClick={handleIssueReferralCode}
                                    >
                                        {t('panel.link.issue')}
                                    </Button>
                                ) : (
                                    <div className="bg-gradient-to-r from-kotomilThemaColor/20 via-kotomilThemaColor/10 to-kotomilThemaColor/20 rounded-lg p-3 border border-kotomilThemaColor/30 shadow-sm w-full overflow-hidden">
                                        <div className="flex items-center justify-between">
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm text-gray-800 break-all">
                                                    {generateReferralLink(referralCode)}
                                                </p>
                                            </div>
                                            <button
                                                onClick={handleCopyCode}
                                                className={`ml-2 p-1 rounded-md transition-all duration-200 flex-shrink-0 min-w-[32px] ${
                                                    isCopied
                                                        ? ' bg-white'
                                                        : 'text-gray-600 hover:text-gray-800 hover:bg-white hover:bg-opacity-50'
                                                }`}
                                                title={
                                                    isCopied
                                                        ? t('panel.link.tooltipCopied')
                                                        : t('panel.link.tooltipCopy')
                                                }
                                            >
                                                {isCopied ? (
                                                    <span className="text-xs font-medium px-1">
                                                        {t('panel.link.copied')}
                                                    </span>
                                                ) : (
                                                    <img
                                                        src={copyIcon}
                                                        alt={t('panel.link.copy')}
                                                        className="w-4 h-4"
                                                    />
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* 紹介数 */}
                        <div className="flex items-center justify-between py-2 border-b border-gray-100">
                            <div className="flex-shrink-0">
                                <span className="text-base text-gray-700">{t('panel.count.label')}</span>
                            </div>
                            <div className="flex-1 ml-4 text-right">
                                <span className="text-gray-800">
                                    <span className="text-2xl">
                                        {numberFormatter.format(Math.min(referralCount, maxReferrals))}
                                    </span>
                                    <span className="text-sm">
                                        {' '}
                                        {t('panel.count.max', {
                                            max: numberFormatter.format(maxReferrals),
                                        })}
                                    </span>
                                </span>
                            </div>
                        </div>

                        {/* ベーシックプラン有効期限 */}
                        <div className="flex items-center justify-between py-2">
                            <div className="flex-shrink-0">
                                <p className="text-sm text-gray-400">{t('panel.plan.label')}</p>
                                <p className="text-base text-gray-700">{t('panel.plan.expiration')}</p>
                            </div>
                            <div className="flex-1 ml-4 text-right">
                                <span
                                    className={`text-sm ${isExpired ? 'text-red-600 font-semibold' : 'text-gray-800'}`}
                                >
                                    {getFormattedExpirationDate()}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* モーダル */}
            <Modal
                isOpen={isInfoOpen}
                onClose={() => setIsInfoOpen(false)}
                contentClassName="w-full max-w-xl p-10"
            >
                <ReferralProgramGuide />
                <div className="mt-12 flex justify-center">
                    <Button
                        variant="secondary"
                        className="lg:px-6 lg:w-40"
                        onClick={() => setIsInfoOpen(false)}
                    >
                        {t('panel.modal.close')}
                    </Button>
                </div>
            </Modal>
        </>
    );
}

export default UserReferralInfo;
